import { useCallback, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 8;

export interface UseImageGridReorderResult {
  containerRef: React.RefObject<HTMLDivElement>;
  dragIndex: number | null;
  hoverIndex: number | null;
  isDragging: boolean;
  getItemProps: (index: number) => {
    draggable: boolean;
    style: React.CSSProperties;
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: () => void;
    onDragEnd: () => void;
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
}

/**
 * Cross-device image reorder: HTML5 drag on desktop + pointer events for touch.
 */
export function useImageGridReorder(
  onReorder?: (fromIndex: number, toIndex: number) => void
): UseImageGridReorderResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const pointerRef = useRef<{
    index: number;
    startX: number;
    startY: number;
    active: boolean;
    pointerId: number;
  } | null>(null);

  const findIndexAtPoint = useCallback((clientX: number, clientY: number): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const children = Array.from(container.children) as HTMLElement[];
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return i;
      }
    }
    return null;
  }, []);

  const commitReorder = useCallback(
    (from: number, to: number) => {
      if (!onReorder || from === to) return;
      onReorder(from, to);
    },
    [onReorder]
  );

  const resetDragState = useCallback(() => {
    setDragIndex(null);
    setHoverIndex(null);
    setIsDragging(false);
    pointerRef.current = null;
  }, []);

  const handleDrop = useCallback(
    (toIndex: number) => {
      if (dragIndex == null || dragIndex === toIndex || !onReorder) return;
      commitReorder(dragIndex, toIndex);
      resetDragState();
    },
    [dragIndex, onReorder, commitReorder, resetDragState]
  );

  const getItemProps = useCallback(
    (index: number) => {
      const reorderEnabled = Boolean(onReorder);
      const isHovered = hoverIndex === index && dragIndex !== null && dragIndex !== index;

      return {
        draggable: reorderEnabled,
        style: {
          cursor: reorderEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
          touchAction: reorderEnabled ? 'none' : 'auto',
          opacity: dragIndex === index && isDragging ? 0.65 : 1,
          outline: isHovered ? '2px solid var(--bs-primary)' : undefined,
          outlineOffset: isHovered ? '2px' : undefined,
        } as React.CSSProperties,
        onDragStart: () => {
          if (!reorderEnabled) return;
          setDragIndex(index);
          setIsDragging(true);
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          if (dragIndex !== null) setHoverIndex(index);
        },
        onDrop: () => handleDrop(index),
        onDragEnd: resetDragState,
        onPointerDown: (e: React.PointerEvent) => {
          if (!reorderEnabled) return;
          const target = e.target as HTMLElement;
          if (target.closest('button')) return;

          pointerRef.current = {
            index,
            startX: e.clientX,
            startY: e.clientY,
            active: false,
            pointerId: e.pointerId,
          };
        },
        onPointerMove: (e: React.PointerEvent) => {
          const state = pointerRef.current;
          if (!state || state.index !== index) return;

          const dx = Math.abs(e.clientX - state.startX);
          const dy = Math.abs(e.clientY - state.startY);

          if (!state.active) {
            if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
            state.active = true;
            setDragIndex(index);
            setIsDragging(true);
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          }

          const over = findIndexAtPoint(e.clientX, e.clientY);
          setHoverIndex(over);
        },
        onPointerUp: (e: React.PointerEvent) => {
          const state = pointerRef.current;
          if (!state || state.index !== index) return;

          if (state.active) {
            const to = findIndexAtPoint(e.clientX, e.clientY);
            if (to !== null) commitReorder(state.index, to);
            try {
              (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
          }

          resetDragState();
        },
        onPointerCancel: () => {
          resetDragState();
        },
      };
    },
    [
      onReorder,
      hoverIndex,
      dragIndex,
      isDragging,
      handleDrop,
      resetDragState,
      findIndexAtPoint,
      commitReorder,
    ]
  );

  return {
    containerRef,
    dragIndex,
    hoverIndex,
    isDragging,
    getItemProps,
  };
}
